import { useEffect, useState } from 'react';

// Enter/exit transition state for a bottom sheet.
//
// Sheets in this app render on a bare `open` boolean, so they appear and
// disappear in one frame: there is no intermediate state for a transition to
// run against on the way in, and nothing is still mounted on the way out. This
// hook supplies both. `mounted` stays true for the length of the exit so the
// sheet can animate down before it leaves the tree; `entered` is the flag the
// sheet transitions on.
//
// Keep SHEET_EXIT_MS in step with the CSS duration the sheet uses on the way
// out. A shorter exit here unmounts mid-animation and the sheet vanishes; a
// longer one leaves an invisible overlay swallowing taps after it has finished
// sliding away.
//
// Asymmetric on purpose. Arriving is the part worth seeing: the sheet travels
// the height of the screen and the eye needs time to follow it, so 340ms reads
// as deliberate where 260 read as a flicker. Leaving is a confirmation of
// something the user already decided, and a slow exit just delays the page
// underneath, so it goes at 240.
export const SHEET_ENTER_MS = 340;
export const SHEET_EXIT_MS = 240;

// A sheet sliding up the full height of the screen is exactly the motion the
// reduced-motion setting exists to suppress, and both iOS and Android surface
// it as an accessibility toggle rather than a niche preference. The sheet still
// mounts and unmounts; it simply does so without travelling.
export function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useSheetTransition(open, exitMs = SHEET_EXIT_MS) {
  const [mounted, setMounted] = useState(Boolean(open));
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);

      // Two frames, not one. The first paints the sheet in its off-screen
      // start position; the second flips the transform so the browser has two
      // distinct computed styles to interpolate between. With a single frame
      // Safari can fold the mount and the style change into one recalculation
      // and skip the transition entirely, which is the bug this replaces.
      let inner;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setEntered(true));
      });
      return () => {
        cancelAnimationFrame(outer);
        if (inner) cancelAnimationFrame(inner);
      };
    }

    setEntered(false);
    const timer = setTimeout(() => setMounted(false), exitMs);
    return () => clearTimeout(timer);
  }, [open, exitMs]);

  return { mounted, entered };
}
